import React, { useState } from 'react';
import { FaArrowLeft, FaPlus, FaTimes } from 'react-icons/fa';
import { useServices } from '../../hooks/useServices';
import '../../styles/CreateServiceModal.css';

const CreateServiceModal = ({ onClose, onSuccess, categories = [] }) => {
  const { createService } = useServices();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Données du formulaire
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    status: 'Activé',
    required_vehicle_types: [],
    required_options: [],
    required_documents: [],
    pricing_type: 'Au Forfait',
    base_price: '',
    minimum_quote: '',
    commission_rate: ''
  });

  const vehicleTypes = ['Engin', 'Camion', 'Camionnette', 'Remorque', 'Voiture', '2 Rous/Tricycle'];
  const requiredOptions = [
    'Type de véhicule / matériel',
    'Modèle de véhicule',
    'Immatriculation',
    'Capacité (poids, volume, passagers...)',
    'Zone de couverture',
    'Informations du Conducteur'
  ];
  const requiredDocuments = [
    'Carte grise',
    'Assurance',
    'Contrôle technique',
    'Permis',
    'CNI'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, item, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], item]
        : prev[field].filter(i => i !== item)
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!formData.name || !formData.category_id) {
        setError('Nom du service et catégorie sont requis');
        return;
      }
    }
    setError('');
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Trouver la catégorie sélectionnée
      const selectedCategory = categories.find(cat => cat.id === formData.category_id);
      
      const serviceData = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        base_price: parseFloat(formData.base_price) || 0,
        price_per_km: formData.pricing_type === 'Au km' ? parseFloat(formData.base_price) || 0 : 0,
        price_per_minute: formData.pricing_type === 'À l\'heure' ? parseFloat(formData.base_price) || 0 : 0,
        pricing_type: formData.pricing_type,
        minimum_quote: parseFloat(formData.minimum_quote) || 0,
        commission_rate: parseFloat(formData.commission_rate) || 0,
        required_vehicle_types: formData.required_vehicle_types,
        required_documents: formData.required_documents
      };

      await createService(serviceData);
      setShowSuccess(true);
      
      // Fermer automatiquement après 2 secondes
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      setError('Erreur lors de la création du service');
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="modal-overlay">
        <div className="modal-content success-modal">
          <div className="success-header">
            <FaArrowLeft className="back-icon" onClick={onClose} />
            <h2>Création de service</h2>
          </div>
          
          <div className="success-content">
            <div className="success-illustration">
              <div className="success-character">
                <div className="character-body"></div>
                <div className="character-arms"></div>
                <div className="trophy"></div>
              </div>
            </div>
            
            <div className="success-text">
              <h3>WaaGo</h3>
              <h4>Félicitation</h4>
              <p>
                Félicitations pour cette belle réussite ! La création de ton service marque
                une étape importante, bravo pour ton engagement et ta détermination.
              </p>
            </div>
            
            <button className="btn-primary" onClick={onSuccess}>
              J'ai compris
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content create-service-modal">
        <div className="modal-header">
          <FaArrowLeft className="back-icon" onClick={onClose} />
          <h2>Création de service</h2>
        </div>

        {currentStep === 1 && (
          <div className="step-content">
            <div className="step-header">
              <h3>Informations générales</h3>
              <div className="step-indicators">
                <span className="step-dot active"></span>
                <span className="step-dot"></span>
              </div>
            </div>

            <div className="form-group">
              <div className="icon-upload">
                <FaPlus />
                <span>Icône ou pictogramme associé</span>
              </div>
            </div>

            <div className="form-group">
              <label>Nom du service</label>
              <input
                type="text"
                placeholder="Ex : Transport"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Courte description</label>
              <textarea
                placeholder="Tapez ici..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Statut</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="status"
                    value="Activé"
                    checked={formData.status === 'Activé'}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  />
                  Activé
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="status"
                    value="Désactivé"
                    checked={formData.status === 'Désactivé'}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  />
                  Désactivé
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Catégorie de véhicule requis</label>
              <div className="checkbox-grid">
                {vehicleTypes.map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.required_vehicle_types.includes(type)}
                      onChange={(e) => handleArrayChange('required_vehicle_types', type, e.target.checked)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Option requis</label>
              <div className="checkbox-list">
                {requiredOptions.map(option => (
                  <label key={option} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.required_options.includes(option)}
                      onChange={(e) => handleArrayChange('required_options', option, e.target.checked)}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Documents requis</label>
              <div className="checkbox-list">
                {requiredDocuments.map(doc => (
                  <label key={doc} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.required_documents.includes(doc)}
                      onChange={(e) => handleArrayChange('required_documents', doc, e.target.checked)}
                    />
                    {doc}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={onClose}>
                Annuler
              </button>
              <button className="btn-primary" onClick={handleNextStep}>
                Suivant
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-content">
            <div className="step-header">
              <h3>Règles de tarification</h3>
              <div className="step-indicators">
                <span className="step-dot"></span>
                <span className="step-dot active"></span>
              </div>
            </div>

            <div className="form-group">
              <label>Type de tarification</label>
              <div className="radio-group-horizontal">
                {['Au Forfait', 'Au km', 'À l\'heure', 'Autre'].map(type => (
                  <label key={type} className="radio-label">
                    <input
                      type="radio"
                      name="pricing_type"
                      value={type}
                      checked={formData.pricing_type === type}
                      onChange={(e) => handleInputChange('pricing_type', e.target.value)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Valeurs par défaut</label>
              <input
                type="number"
                placeholder="0,00 CFA"
                value={formData.base_price}
                onChange={(e) => handleInputChange('base_price', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Devis minimum requis</label>
              <input
                type="number"
                placeholder="0,00 CFA"
                value={formData.minimum_quote}
                onChange={(e) => handleInputChange('minimum_quote', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Commission prélevée</label>
              <p className="form-help">Taux fixe ou pourcentage prélevé par la plateforme</p>
              <input
                type="number"
                placeholder="0 %"
                value={formData.commission_rate}
                onChange={(e) => handleInputChange('commission_rate', e.target.value)}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button className="btn-secondary" onClick={handlePreviousStep}>
                Précédent
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateServiceModal;